import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import {
  Users,
  UserCheck,
  Coins,
  Wallet,
  Plus,
  Save,
  ShieldCheck,
  UserRound,
  Globe,
  Repeat,
  Trash2,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import StaffPermissionsDialog from "@/components/StaffPermissionsDialog";
import EditStaffDialog from "@/components/EditStaffDialog";
import ApprovalsPanel from "@/components/ApprovalsPanel";
import ReferralApprovalsCard from "@/components/ReferralApprovalsCard";
import MemberPointsDialog from "@/components/MemberPointsDialog";
import LedgerPanel from "@/components/LedgerPanel";
import ReferralsPanel from "@/components/ReferralsPanel";
import WhatsAppSettingsCard from "@/components/WhatsAppSettingsCard";
import WaTemplatesCard from "@/components/WaTemplatesCard";
import BrandingCard from "@/components/BrandingCard";
import { ReferralDaysChip, isPendingReferee } from "@/components/ReferralExpiry";
import EmailSettingsCard from "@/components/EmailSettingsCard";
import FeatureScopeButton from "@/components/FeatureScopeButton";
import OffersPanel from "@/components/OffersPanel";
import ServicesPanel from "@/components/ServicesPanel";
import LandingCmsPanel from "@/components/LandingCmsPanel";
import BookingEnquiriesSection from "@/components/BookingEnquiriesSection";
import OfferEnquiriesSection from "@/components/OfferEnquiriesSection";
import { useAuth } from "@/lib/auth-context";

function StatCard({ icon: Icon, label, value, accent, trend }) {
  return (
    <div
      className="w-card w-card-bordered p-5 lift"
      style={
        accent
          ? {
              background:
                "linear-gradient(140deg, rgba(201,162,75,0.10), var(--surface))",
              borderColor: "rgba(201,162,75,0.4)",
            }
          : undefined
      }
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="label-caps">{label}</div>
          <div className="mt-2 num-big">{value.toLocaleString()}</div>
          {trend != null && (
            <div className={`text-xs font-medium mt-1 ${trend >= 0 ? "text-emerald-700 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
              {trend >= 0 ? "▲" : "▼"} {Math.abs(trend)}% vs prior 30d
            </div>
          )}
        </div>
        <div className="h-10 w-10 rounded-xl bg-navy text-white grid place-items-center">
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </div>
  );
}

const emptyCust = {
  name: "",
  phone: "",
  email: "",
  password: "",
  status: "Active",
  country_code: "AE",
  preferred_currency: "AED",
  preferred_channel: "WhatsApp",
  gdpr_consent: false,
};
const emptyStaff = { username: "", password: "", name: "", role: "staff" };
const emptyCountry = { code: "", name: "", default_currency: "", gdpr_covered: false };
const emptyRate = { currency: "", rate_to_aed: "" };

export default function AdminDashboard() {
  const { user } = useAuth();
  // Initialise with safe defaults so the dashboard renders IMMEDIATELY after
  // login — even if any API call is slow, throws, or returns 403. This
  // prevents the "blank dashboard on manager login" bug: without defaults,
  // `!stats` would render only skeletons and any unexpected error would leave
  // the page permanently empty.
  const [stats, setStats] = useState({
    total_customers: 0,
    active_members: 0,
    total_points_issued: 0,
    total_points_redeemed: 0,
    trend_pct: null,
  });
  const [customers, setCustomers] = useState([]);
  const [pendingOnly, setPendingOnly] = useState(false);
  const [pendingList, setPendingList] = useState([]);
  const [staff, setStaff] = useState([]);
  const [txs, setTxs] = useState([]);
  const [config, setConfig] = useState(null);
  const [countries, setCountries] = useState([]);
  const [rates, setRates] = useState([]);
  const [offers, setOffers] = useState([]);
  const [tab, setTab] = useState("customers");
  const [offerPreset, setOfferPreset] = useState(null);
  const addOfferItem = (section) => { setOfferPreset({ section, at: Date.now() }); setTab("offers"); };
  const [activity, setActivity] = useState([]);
  const [refAlerts, setRefAlerts] = useState(0);
  const loadRefAlerts = () => api.get("/referrals/approvals").then(({ data }) => setRefAlerts(data.eligible_count)).catch(() => setRefAlerts(0));
  useEffect(() => { loadRefAlerts(); }, [tab]);

  const [newCust, setNewCust] = useState(emptyCust);
  const [newStaff, setNewStaff] = useState(emptyStaff);
  const [newCountry, setNewCountry] = useState(emptyCountry);
  const [newRate, setNewRate] = useState(emptyRate);
  const [custDialog, setCustDialog] = useState(false);
  const [staffDialog, setStaffDialog] = useState(false);
  const [editCust, setEditCust] = useState(null);

  const refresh = async () => {
    // Fetch every dashboard resource in parallel, but do it with allSettled
    // so a single flaky/failing endpoint (transient 5xx, network hiccup,
    // permission mismatch after a role change, etc.) can't blank out the
    // entire dashboard. We surface failures as toasts, and always populate
    // whatever we could fetch — never leaving `stats` permanently null.
    const jobs = [
      ["stats", () => api.get("/admin/stats")],
      ["customers", () => api.get("/customers")],
      ["staff", () => api.get("/staff")],
      ["txs", () => api.get("/transactions")],
      ["config", () => api.get("/config")],
      ["countries", () => api.get("/config/countries")],
      ["rates", () => api.get("/config/exchange-rates")],
      ["offers", () => api.get("/offers")],
      ["activity", () => api.get("/activity-log")],
    ];
    const results = await Promise.allSettled(jobs.map(([, fn]) => fn()));
    const setters = {
      stats: setStats, customers: setCustomers, staff: setStaff,
      txs: setTxs, config: setConfig, countries: setCountries,
      rates: setRates, offers: setOffers, activity: setActivity,
    };
    const empty = {
      stats: { total_customers: 0, active_members: 0, total_points_issued: 0,
               total_points_redeemed: 0, trend_pct: null },
      customers: [], staff: [], txs: [], config: null, countries: [],
      rates: [], offers: [], activity: [],
    };
    const failed = [];
    results.forEach((r, i) => {
      const [key] = jobs[i];
      if (r.status === "fulfilled") {
        setters[key](r.value.data);
      } else {
        // Fall back to an empty/skeleton value so the render can proceed.
        setters[key](empty[key]);
        failed.push({ key, err: r.reason });
      }
    });
    if (failed.length) {
      const first = failed[0];
      const msg = formatError(first.err);
      const rest = failed.length > 1 ? ` (+${failed.length - 1} more)` : "";
      toast.error(`Couldn't load ${first.key}: ${msg}${rest}`);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  // Auto-suggest currency when country changes
  const onCountryChange = (code, setter, current) => {
    const found = countries.find((c) => c.code === code);
    setter({
      ...current,
      country_code: code,
      preferred_currency: found?.default_currency || current.preferred_currency,
    });
  };

  const submitNewCust = async (e) => {
    e.preventDefault();
    try {
      await api.post("/customers", newCust);
      toast.success("Member enrolled");
      setCustDialog(false);
      setNewCust(emptyCust);
      refresh();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const submitNewStaff = async (e) => {
    e.preventDefault();
    try {
      await api.post("/staff", newStaff);
      toast.success(`${newStaff.role === "admin" ? "Admin" : "Staff"} account created`);
      setStaffDialog(false);
      setNewStaff(emptyStaff);
      refresh();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const toggleStaff = async (u) => {
    try {
      await api.patch(`/staff/${u.id}`, { active: !u.active });
      toast.success(`${u.username} ${!u.active ? "activated" : "deactivated"}`);
      refresh();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const saveEditCust = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: editCust.name,
        phone: editCust.phone,
        email: editCust.email,
        status: editCust.status,
        country_code: editCust.country_code || null,
        preferred_currency: editCust.preferred_currency || null,
        preferred_channel: editCust.preferred_channel,
        gdpr_consent: !!editCust.gdpr_consent,
      };
      await api.patch(`/customers/${editCust.customer_id}`, payload);
      toast.success("Member updated");
      setEditCust(null);
      refresh();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const saveConfig = async (e) => {
    e.preventDefault();
    try {
      await api.patch("/config", {
        default_rule: config.default_rule,
        service_rules: config.service_rules || {},
        max_points_per_transaction: parseInt(config.max_points_per_transaction, 10) || 10000,
        min_invoice_amount: parseFloat(config.min_invoice_amount) || 0,
        referral_customer_bonus: parseInt(config.referral_customer_bonus, 10) || 0,
        referral_vendor_bonus: parseInt(config.referral_vendor_bonus, 10) || 0,
        point_value_aed: Math.max(0.0001, parseFloat(config.point_value_aed) || 1.0),
        referral_referrer_points: parseInt(config.referral_referrer_points, 10) || 0,
        referral_referee_points: parseInt(config.referral_referee_points, 10) || 0,
        redemption_requires_approval: !!config.redemption_requires_approval,
        referral_window_days: Math.max(1, parseInt(config.referral_window_days, 10) || 60),
        referral_min_purchase_aed: Math.max(0, parseFloat(config.referral_min_purchase_aed) || 0),
      });
      toast.success("Reward rule updated");
      refresh();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const submitNewCountry = async (e) => {
    e.preventDefault();
    try {
      await api.post("/config/countries", {
        code: newCountry.code.toUpperCase().trim(),
        name: newCountry.name.trim(),
        default_currency: newCountry.default_currency.toUpperCase().trim(),
        gdpr_covered: !!newCountry.gdpr_covered,
      });
      toast.success("Country added");
      setNewCountry(emptyCountry);
      refresh();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const patchCountry = async (code, updates) => {
    try {
      await api.patch(`/config/countries/${code}`, updates);
      refresh();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const deleteCountry = async (code) => {
    if (!window.confirm(`Delete country ${code}?`)) return;
    try {
      await api.delete(`/config/countries/${code}`);
      toast.success("Removed");
      refresh();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const submitNewRate = async (e) => {
    e.preventDefault();
    const code = newRate.currency.toUpperCase().trim();
    try {
      await api.put(`/config/exchange-rates/${code}`, {
        currency: code,
        rate_to_aed: parseFloat(newRate.rate_to_aed),
      });
      toast.success(`Rate saved: 1 ${code} = ${newRate.rate_to_aed} AED`);
      setNewRate(emptyRate);
      refresh();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const updateRate = async (currency, rate) => {
    try {
      await api.put(`/config/exchange-rates/${currency}`, {
        currency,
        rate_to_aed: parseFloat(rate),
      });
      toast.success(`${currency} rate updated`);
      refresh();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const deleteRate = async (currency) => {
    if (!window.confirm(`Delete rate for ${currency}?`)) return;
    try {
      await api.delete(`/config/exchange-rates/${currency}`);
      toast.success("Rate removed");
      refresh();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  // Live label describing the point ↔ AED rate, driven by the Admin setting.
  const rateLabel = (() => {
    const pv = Math.max(0.0001, parseFloat(config?.point_value_aed) || 1.0);
    const aedPerPt = pv.toFixed(2);
    const ptsPerAed = 1 / pv;
    const ptsPerAedStr = Number.isInteger(ptsPerAed) ? ptsPerAed.toString() : ptsPerAed.toFixed(2);
    return `1 pt = ${aedPerPt} AED · 1 AED = ${ptsPerAedStr} pts`;
  })();

  return (
    <AppShell
      title="Admin Console"
      subtitle="Members, staff, reward rules and the full ledger."
    >
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
        <StatCard icon={Users} label="Total Customers" value={stats.total_customers} />
        <StatCard icon={UserCheck} label="Active Members" value={stats.active_members} />
        <StatCard icon={Coins} label="Points Issued" value={stats.total_points_issued} accent trend={stats.trend_pct} />
        <StatCard icon={Wallet} label="Points Redeemed" value={stats.total_points_redeemed} />
      </section>

      <Tabs value={tab} onValueChange={setTab} className="mt-10">
        <TabsList
          className="rounded-xl p-1"
          style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
        >
          <TabsTrigger value="customers" data-testid="tab-customers">Members</TabsTrigger>
          <TabsTrigger value="approvals" data-testid="tab-approvals">
            Approvals
            {refAlerts > 0 && <span className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-600 text-white"
                                    title="Referrals eligible for approval" data-testid="tab-approvals-referral-badge">{refAlerts}</span>}
          </TabsTrigger>
          <TabsTrigger value="staff" data-testid="tab-staff">Staff</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="tab-transactions">Ledger</TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-config">Reward Rule</TabsTrigger>
          <TabsTrigger value="offers" data-testid="tab-offers">Offers</TabsTrigger>
          <TabsTrigger value="services" data-testid="tab-services">Services</TabsTrigger>
          <TabsTrigger value="landing" data-testid="tab-landing">Landing Page</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">Activity Log</TabsTrigger>
        </TabsList>

        {/* ───── Customers ───── */}
        <TabsContent value="customers" className="mt-6">
          <div className="flex items-center gap-2 flex-wrap mb-4">
            <h2 className="text-xl font-bold mr-auto" style={{ letterSpacing: "-0.01em" }}>
              Members
            </h2>
            <Dialog open={custDialog} onOpenChange={setCustDialog}>
              <DialogTrigger asChild>
                <Button
                  data-testid="add-customer-btn"
                  className="bg-navy hover:opacity-90 text-white rounded-xl"
                >
                  <Plus className="h-4 w-4 mr-1.5" /> New member
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="new-customer-dialog" className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Enrol a new member</DialogTitle>
                </DialogHeader>
                <form onSubmit={submitNewCust} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label>Full name</Label>
                    <Input
                      value={newCust.name}
                      onChange={(e) => setNewCust({ ...newCust, name: e.target.value })}
                      required
                      data-testid="new-cust-name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Phone</Label>
                      <Input
                        value={newCust.phone}
                        onChange={(e) => setNewCust({ ...newCust, phone: e.target.value })}
                        required
                        data-testid="new-cust-phone"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={newCust.email}
                        onChange={(e) => setNewCust({ ...newCust, email: e.target.value })}
                        required
                        data-testid="new-cust-email"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Country</Label>
                      <Select
                        value={newCust.country_code}
                        onValueChange={(v) => onCountryChange(v, setNewCust, newCust)}
                      >
                        <SelectTrigger data-testid="new-cust-country">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map((c) => (
                            <SelectItem key={c.code} value={c.code}>
                              {c.name}
                              {c.gdpr_covered ? " · GDPR" : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Preferred currency</Label>
                      <Select
                        value={newCust.preferred_currency}
                        onValueChange={(v) => setNewCust({ ...newCust, preferred_currency: v })}
                      >
                        <SelectTrigger data-testid="new-cust-currency">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {rates.map((r) => (
                            <SelectItem key={r.currency} value={r.currency}>
                              {r.currency}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Preferred channel</Label>
                      <Select
                        value={newCust.preferred_channel}
                        onValueChange={(v) => setNewCust({ ...newCust, preferred_channel: v })}
                      >
                        <SelectTrigger data-testid="new-cust-channel">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                          <SelectItem value="Email">Email</SelectItem>
                          <SelectItem value="SMS">SMS</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Status</Label>
                      <Select
                        value={newCust.status}
                        onValueChange={(v) => setNewCust({ ...newCust, status: v })}
                      >
                        <SelectTrigger data-testid="new-cust-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Active">Active</SelectItem>
                          <SelectItem value="Inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Portal password</Label>
                    <Input
                      value={newCust.password}
                      onChange={(e) => setNewCust({ ...newCust, password: e.target.value })}
                      placeholder="temp1234"
                      required
                      data-testid="new-cust-password"
                    />
                  </div>
                  <label
                    className="flex items-start gap-3 rounded-xl p-3 cursor-pointer"
                    style={{ background: "var(--bg-soft)" }}
                  >
                    <Switch
                      checked={newCust.gdpr_consent}
                      onCheckedChange={(v) => setNewCust({ ...newCust, gdpr_consent: v })}
                      data-testid="new-cust-gdpr"
                    />
                    <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                      <div className="font-medium" style={{ color: "var(--text)" }}>
                        Marketing consent (GDPR)
                      </div>
                      Only marketing/promotional messages are gated by this. Transactional
                      messages (balance updates, redemptions) are always allowed.
                    </div>
                  </label>
                  <DialogFooter>
                    <Button
                      type="submit"
                      className="bg-navy hover:opacity-90 text-white rounded-xl"
                      data-testid="submit-new-cust"
                    >
                      Create member
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            <Button
              type="button"
              variant="outline"
              aria-pressed={pendingOnly}
              className={`rounded-full h-10 ml-2 ${pendingOnly ? "bg-navy text-white hover:bg-navy hover:text-white" : ""}`}
              data-testid="admin-pending-referrals-filter"
              onClick={async () => {
                if (pendingOnly) { setPendingOnly(false); return; }
                try {
                  const { data } = await api.get("/customers", { params: { referral: "pending" } });
                  setPendingList(data);
                  setPendingOnly(true);
                } catch (e) { toast.error(formatError(e)); }
              }}
            >
              Pending referrals
            </Button>
            <Button
              type="button"
              variant="outline"
              className="rounded-full h-10 border-gold text-gold hover:bg-gold/10 ml-2"
              data-testid="members-csv-export"
              onClick={async () => {
                try {
                  const { data } = await api.get("/reports/members.csv",
                    { responseType: "blob" });
                  const url = URL.createObjectURL(data);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "zola-members.csv";
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success("Members CSV downloaded");
                } catch (e) {
                  toast.error(formatError(e));
                }
              }}
            >
              Export CSV
            </Button>
          </div>

          <div className="w-card-flat overflow-x-auto">
            <table className="w-full text-sm" data-testid="customers-table">
              <thead style={{ background: "var(--bg-soft)" }}>
                <tr>
                  <th className="text-left px-4 py-3 label-caps">Customer</th>
                  <th className="text-left px-4 py-3 label-caps">Contact</th>
                  <th className="text-left px-4 py-3 label-caps">Region</th>
                  <th className="text-right px-4 py-3 label-caps">Balance</th>
                  <th className="text-left px-4 py-3 label-caps">Status</th>
                  <th className="text-right px-4 py-3 label-caps">Action</th>
                </tr>
              </thead>
              <tbody>
                {(pendingOnly ? pendingList : customers).map((c) => (
                  <tr key={c.id} style={{ borderTop: "1px solid var(--border)" }}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{c.name}</div>
                      <div className="font-mono text-xs"
                           style={{ color: "var(--text-muted)" }}>
                        {c.customer_id}
                      </div>
                      {isPendingReferee(c) && (
                        <div className="mt-1"><ReferralDaysChip expiresAt={c.referral_expires_at} testid={`admin-referral-days-${c.customer_id}`} /></div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: "var(--text-muted)" }}>
                      <div>{c.email}</div>
                      <div>{c.phone}</div>
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: "var(--text-muted)" }}>
                      <div>{c.country_code || "—"} · {c.preferred_currency || "—"}</div>
                      <div>{c.preferred_channel || "—"} {c.gdpr_consent ? "· consent ✓" : ""}</div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="num-big">{c.reward_balance}</div>
                      <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                        lifetime {c.lifetime_earned}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs ${
                          c.status === "Active"
                            ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                            : "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                        }`}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${
                          c.status === "Active" ? "bg-emerald-500" : "bg-red-500"
                        }`} />
                        {c.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2 justify-end flex-wrap">
                        <MemberPointsDialog
                          member={c}
                          config={config}
                          rates={rates}
                          onDone={refresh}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-full"
                          onClick={() => setEditCust({ ...c })}
                          data-testid={`edit-customer-${c.customer_id}`}
                        >
                          Edit
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Dialog open={!!editCust} onOpenChange={(o) => !o && setEditCust(null)}>
            <DialogContent data-testid="edit-customer-dialog" className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Edit member</DialogTitle>
              </DialogHeader>
              {editCust && (
                <form onSubmit={saveEditCust} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label>Name</Label>
                    <Input
                      value={editCust.name || ""}
                      onChange={(e) => setEditCust({ ...editCust, name: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Phone</Label>
                      <Input
                        value={editCust.phone || ""}
                        onChange={(e) => setEditCust({ ...editCust, phone: e.target.value })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Email</Label>
                      <Input
                        value={editCust.email || ""}
                        onChange={(e) => setEditCust({ ...editCust, email: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Country</Label>
                      <Select
                        value={editCust.country_code || ""}
                        onValueChange={(v) => onCountryChange(v, setEditCust, editCust)}
                      >
                        <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                        <SelectContent>
                          {countries.map((c) => (
                            <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Preferred currency</Label>
                      <Select
                        value={editCust.preferred_currency || ""}
                        onValueChange={(v) => setEditCust({ ...editCust, preferred_currency: v })}
                      >
                        <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                        <SelectContent>
                          {rates.map((r) => (
                            <SelectItem key={r.currency} value={r.currency}>{r.currency}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Preferred channel</Label>
                      <Select
                        value={editCust.preferred_channel || "WhatsApp"}
                        onValueChange={(v) => setEditCust({ ...editCust, preferred_channel: v })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                          <SelectItem value="Email">Email</SelectItem>
                          <SelectItem value="SMS">SMS</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Status</Label>
                      <Select
                        value={editCust.status}
                        onValueChange={(v) => setEditCust({ ...editCust, status: v })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Active">Active</SelectItem>
                          <SelectItem value="Inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <label className="flex items-start gap-3 rounded-xl p-3 cursor-pointer"
                         style={{ background: "var(--bg-soft)" }}>
                    <Switch
                      checked={!!editCust.gdpr_consent}
                      onCheckedChange={(v) => setEditCust({ ...editCust, gdpr_consent: v })}
                      data-testid="edit-cust-gdpr"
                    />
                    <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                      <div className="font-medium" style={{ color: "var(--text)" }}>
                        Marketing consent (GDPR)
                      </div>
                      {editCust.gdpr_consent_at && (
                        <div>Given {new Date(editCust.gdpr_consent_at).toLocaleDateString()}</div>
                      )}
                    </div>
                  </label>
                  <DialogFooter>
                    <Button
                      type="submit"
                      className="bg-navy hover:opacity-90 text-white rounded-xl"
                      data-testid="submit-edit-cust"
                    >
                      <Save className="h-4 w-4 mr-1.5" /> Save changes
                    </Button>
                  </DialogFooter>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ───── Staff ───── */}
        <TabsContent value="approvals" className="mt-6">
          <ReferralApprovalsCard onChanged={() => { refresh(); loadRefAlerts(); }} />
          <ApprovalsPanel config={config} onBalanceChange={refresh} />
        </TabsContent>

        <TabsContent value="staff" className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">Staff Accounts</h2>
            <Dialog open={staffDialog} onOpenChange={setStaffDialog}>
              <DialogTrigger asChild>
                <Button
                  data-testid="add-staff-btn"
                  disabled={!user?.profile?.super_admin}
                  title={!user?.profile?.super_admin ? "Only the super admin can create accounts" : undefined}
                  className="bg-navy hover:opacity-90 text-white rounded-xl"
                >
                  <Plus className="h-4 w-4 mr-1.5" /> New account
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="new-staff-dialog">
                <DialogHeader>
                  <DialogTitle>Create staff / admin login</DialogTitle>
                </DialogHeader>
                <form onSubmit={submitNewStaff} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Username</Label>
                      <Input
                        value={newStaff.username}
                        onChange={(e) => setNewStaff({ ...newStaff, username: e.target.value })}
                        required
                        data-testid="new-staff-username"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Full name</Label>
                      <Input
                        value={newStaff.name}
                        onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })}
                        required
                        data-testid="new-staff-name"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Password</Label>
                      <Input
                        value={newStaff.password}
                        onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })}
                        required
                        data-testid="new-staff-password"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Role</Label>
                      <Select
                        value={newStaff.role}
                        onValueChange={(v) => setNewStaff({ ...newStaff, role: v })}
                      >
                        <SelectTrigger data-testid="new-staff-role"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="staff">Staff</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      type="submit"
                      className="bg-navy hover:opacity-90 text-white rounded-xl"
                      data-testid="submit-new-staff"
                    >
                      Create account
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="w-card-flat overflow-x-auto">
            <table className="w-full text-sm" data-testid="staff-table">
              <thead style={{ background: "var(--bg-soft)" }}>
                <tr>
                  <th className="text-left px-4 py-3 label-caps">User</th>
                  <th className="text-left px-4 py-3 label-caps">Role</th>
                  <th className="text-left px-4 py-3 label-caps">Created</th>
                  <th className="text-right px-4 py-3 label-caps">Permissions</th>
                  <th className="text-right px-4 py-3 label-caps">Active</th>
                </tr>
              </thead>
              <tbody>
                {staff.map((u) => (
                  <tr key={u.id} style={{ borderTop: "1px solid var(--border)" }}
                      data-testid={`staff-row-${u.username}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{u.name}</div>
                      <div className="font-mono text-xs" style={{ color: "var(--text-muted)" }}>
                        {u.username}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs"
                            style={{ background: "var(--bg-soft)" }}>
                        {u.role === "admin"
                          ? <ShieldCheck className="h-3 w-3" />
                          : <UserRound className="h-3 w-3" />}
                        {u.super_admin ? "super admin" : u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: "var(--text-muted)" }}>
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2 justify-end flex-wrap">
                        <EditStaffDialog
                          user={u}
                          currentUserId={user?.id}
                          onSaved={(fresh) => {
                            setStaff((prev) => prev.map((s) => (s.id === fresh.id ? { ...s, ...fresh } : s)));
                          }}
                        />
                        <StaffPermissionsDialog user={u} onSaved={(fresh) => {
                          setStaff((prev) => prev.map((s) => (s.id === fresh.id ? { ...s, ...fresh } : s)));
                        }} />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Switch
                        checked={u.active}
                        disabled={
                          (u.super_admin && u.id !== user?.id) ||
                          (!user?.profile?.super_admin && u.id !== user?.id)
                        }
                        onCheckedChange={() => toggleStaff(u)}
                        data-testid={`toggle-staff-${u.username}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ───── Ledger ───── */}
        <TabsContent value="transactions" className="mt-6">
          <ReferralsPanel onChanged={refresh} />
          <LedgerPanel staff={staff} canReverse={user?.role === "admin"} onChanged={refresh} />
        </TabsContent>

        {/* ───── Reward rule ───── */}
        <TabsContent value="config" className="mt-6">
          <div className="max-w-3xl">
            <h2 className="text-xl font-bold mb-4">Reward Rules</h2>
            <form onSubmit={saveConfig} className="w-card w-card-bordered p-6 space-y-6"
                  data-testid="config-form">
              <div>
                <div className="label-caps mb-3">Default rule (applies unless a service-specific rule matches)</div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Type</Label>
                    <Select
                      value={config?.default_rule?.type || "percentage"}
                      onValueChange={(v) => setConfig({ ...config, default_rule: { ...(config.default_rule||{}), type: v } })}
                    >
                      <SelectTrigger data-testid="config-default-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage of purchase</SelectItem>
                        <SelectItem value="fixed">Fixed points per transaction</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Value {config?.default_rule?.type === "fixed" ? "(pts)" : "(%)"}</Label>
                    <Input
                      type="number" step="0.01" min="0"
                      value={config?.default_rule?.value ?? ""}
                      onChange={(e) => setConfig({ ...config, default_rule: { ...(config.default_rule||{}), value: parseFloat(e.target.value)||0 } })}
                      data-testid="config-default-value"
                    />
                  </div>
                </div>
                <p className="text-xs mt-2" style={{ color: "var(--text-muted)" }}>
                  Applied to AED-normalized invoice amount. Current rate: <span className="font-semibold" data-testid="config-default-rate-label">{rateLabel}</span>.
                </p>
              </div>

              <div>
                <div className="label-caps mb-3">Service-specific rules</div>
                <div className="space-y-3">
                  {["Flights","Hotels","Packages","Visa","Other"].map((svc) => {
                    const rule = config?.service_rules?.[svc] || { type: "percentage", value: 0 };
                    return (
                      <div key={svc} className="grid grid-cols-[110px_1fr_1fr] gap-3 items-center">
                        <div className="text-sm font-medium">{svc}</div>
                        <Select
                          value={rule.type}
                          onValueChange={(v) => setConfig({ ...config, service_rules: { ...(config.service_rules||{}), [svc]: { ...rule, type: v } } })}
                        >
                          <SelectTrigger data-testid={`config-svc-type-${svc}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percentage">Percentage</SelectItem>
                            <SelectItem value="fixed">Fixed pts</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          type="number" step="0.01" min="0"
                          value={rule.value ?? ""}
                          onChange={(e) => setConfig({ ...config, service_rules: { ...(config.service_rules||{}), [svc]: { ...rule, value: parseFloat(e.target.value)||0 } } })}
                          data-testid={`config-svc-value-${svc}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Max points per transaction (safety cap)</Label>
                  <Input
                    type="number" min="1"
                    value={config?.max_points_per_transaction ?? ""}
                    onChange={(e) => setConfig({ ...config, max_points_per_transaction: e.target.value })}
                    data-testid="config-max-points"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Min invoice amount (AED)</Label>
                  <Input
                    type="number" min="0" step="0.01"
                    value={config?.min_invoice_amount ?? 0}
                    onChange={(e) => setConfig({ ...config, min_invoice_amount: e.target.value })}
                    data-testid="config-min-invoice"
                  />
                </div>
              </div>

              <div className="rounded-xl p-4 space-y-3"
                   style={{ background: "var(--bg-soft)", border: "1px solid var(--border)" }}>
                <Label className="label-caps">Referral points</Label>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="space-y-1.5">
                    <Label>Referrer points</Label>
                    <Input type="number" min="0" value={config?.referral_referrer_points ?? 0}
                           onChange={(e) => setConfig({ ...config, referral_referrer_points: e.target.value })}
                           data-testid="config-ref-referrer" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Referee points</Label>
                    <Input type="number" min="0" value={config?.referral_referee_points ?? 0}
                           onChange={(e) => setConfig({ ...config, referral_referee_points: e.target.value })}
                           data-testid="config-ref-referee" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Referral validity (days)</Label>
                    <Input type="number" min="1" value={config?.referral_window_days ?? 60}
                           onChange={(e) => setConfig({ ...config, referral_window_days: e.target.value })}
                           data-testid="config-referral-window" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Minimum purchase (AED)</Label>
                    <Input type="number" min="0" step="0.01" value={config?.referral_min_purchase_aed ?? 0}
                           onChange={(e) => setConfig({ ...config, referral_min_purchase_aed: e.target.value })}
                           data-testid="config-referral-min-purchase" />
                  </div>
                </div>
                <p className="text-xs" style={{ color: "var(--text-muted)" }} data-testid="config-referral-help">
                  {Number(config?.referral_min_purchase_aed) > 0
                    ? "When the referee's total credited purchases reach the minimum within the validity period, the referral is marked Eligible for Approval. Points are credited only after an Admin or Manager approves it in the Approvals tab."
                    : "With the minimum at 0, both sides are credited automatically when the referee's first purchase is credited within the validity period. After that the referral expires."}
                </p>
              </div>

              <div className="rounded-xl p-4"
                   style={{ background: "var(--bg-soft)", border: "1px solid var(--border)" }} data-testid="config-redeem-confirm-note">
                <Label className="label-caps">Redemptions</Label>
                <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                  Staff confirm every redemption in a pop-up (member, points, AED value). Confirmed redemptions are approved
                  and deducted immediately, and recorded with the staff name and time.
                </p>
              </div>

              <div className="rounded-xl p-4"
                   style={{
                     background: "rgba(201,162,75,0.10)",
                     border: "1px solid rgba(201,162,75,0.35)",
                   }}>
                <Label className="label-caps gold">Point value</Label>
                <div className="grid grid-cols-2 gap-3 mt-2 items-end">
                  <div className="space-y-1.5">
                    <Label>1 point equals (AED)</Label>
                    <Input
                      type="number" step="0.01" min="0.01"
                      value={config?.point_value_aed ?? 1}
                      onChange={(e) => setConfig({ ...config, point_value_aed: e.target.value })}
                      data-testid="config-point-value"
                    />
                  </div>
                  <div className="text-xs pb-2" style={{ color: "var(--text-muted)" }}>
                    Current rate: <span className="font-semibold" data-testid="config-point-value-label">{rateLabel}</span>.
                    Changes apply to <em>new</em> transactions only — existing
                    balances are never restated.
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                className="rounded-xl text-navy hover:opacity-90"
                style={{ background: "var(--gold)" }}
                data-testid="save-config"
              >
                <Save className="h-4 w-4 mr-1.5" /> Save rules
              </Button>
            </form>
            <WhatsAppSettingsCard />
            <WaTemplatesCard />
            <BrandingCard />
            <EmailSettingsCard />
            <FeatureScopeButton />
          </div>
        </TabsContent>

        {/* ───── Offers ───── */}
        <TabsContent value="landing" className="mt-6">
          <LandingCmsPanel onAddItem={addOfferItem} />
        </TabsContent>

        <TabsContent value="services" className="mt-6">
          <ServicesPanel />
        </TabsContent>

        <TabsContent value="offers" className="mt-6">
          <OffersPanel offers={offers} refresh={refresh} currencies={rates.map((r) => r.currency)} preset={offerPreset} />
        </TabsContent>

        {/* ───── Reports & Analytics ───── */}
        <TabsContent value="reports" className="mt-6">
          <ReportsPanel />
        </TabsContent>

        {/* ───── Activity Log ───── */}
        <TabsContent value="activity" className="mt-6">
          <h2 className="text-xl font-bold mb-4">Activity Log</h2>
          <div className="w-card-flat overflow-x-auto">
            <table className="w-full text-sm" data-testid="activity-table">
              <thead style={{ background: "var(--bg-soft)" }}>
                <tr>
                  <th className="text-left px-4 py-3 label-caps">When</th>
                  <th className="text-left px-4 py-3 label-caps">Actor</th>
                  <th className="text-left px-4 py-3 label-caps">Action</th>
                  <th className="text-left px-4 py-3 label-caps">Target</th>
                </tr>
              </thead>
              <tbody>
                {activity.length === 0 && (
                  <tr><td colSpan={4} className="p-6 text-center text-sm"
                        style={{ color: "var(--text-muted)" }}>No activity recorded yet.</td></tr>
                )}
                {activity.map((a) => (
                  <tr key={a.id} style={{ borderTop: "1px solid var(--border)" }}>
                    <td className="px-4 py-3 text-xs" style={{ color: "var(--text-muted)" }}>
                      {new Date(a.timestamp).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{a.actor_username}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium"
                            style={{ background: "var(--bg-soft)" }}>{a.action_type}</span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{a.target_id || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}

function ReportsPanel() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [exporting, setExporting] = React.useState(false);
  const panelRef = React.useRef(null);
  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/reports");
        setData(data);
      } catch (e) { toast.error(formatError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const exportPdf = async () => {
    if (!panelRef.current) return;
    setExporting(true);
    try {
      const [{ toPng }, jsPDFmod] = await Promise.all([
        import("html-to-image"),
        import("jspdf"),
      ]);
      const JsPDF = jsPDFmod.jsPDF;
      const dataUrl = await toPng(panelRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: getComputedStyle(document.body).getPropertyValue("--bg") || "#ffffff",
      });
      const img = new Image();
      img.src = dataUrl;
      await new Promise((res) => { img.onload = res; });
      const pdf = new JsPDF({ orientation: "p", unit: "mm", format: "a4" });
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentW = pw - margin * 2;
      const ratio = img.height / img.width;
      const imgH = contentW * ratio;

      // Header
      pdf.setFillColor(11, 36, 71);
      pdf.rect(0, 0, pw, 18, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(14);
      pdf.setFont(undefined, "bold");
      pdf.text("Zola Rewards — Reports & Analytics", margin, 11);
      pdf.setFontSize(9);
      pdf.setFont(undefined, "normal");
      pdf.setTextColor(201, 162, 75);
      pdf.text(new Date().toLocaleString(), pw - margin, 11, { align: "right" });

      // Image
      let y = 24;
      if (imgH <= ph - y - margin) {
        pdf.addImage(dataUrl, "PNG", margin, y, contentW, imgH);
      } else {
        // paginate the image if taller than one page
        const pageContentH = ph - y - margin;
        let drawn = 0;
        while (drawn < imgH) {
          const sliceH = Math.min(pageContentH, imgH - drawn);
          pdf.addImage(dataUrl, "PNG", margin, y - drawn, contentW, imgH);
          drawn += sliceH;
          if (drawn < imgH) {
            pdf.addPage();
            y = margin;
          }
        }
      }

      // Footer on last page
      pdf.setTextColor(140, 140, 140);
      pdf.setFontSize(8);
      pdf.text("Zola Safari Tours · India & UAE · enquiry@zolasafari.com",
              margin, ph - 5);

      pdf.save(`zola-reports-${new Date().toISOString().slice(0,10)}.pdf`);
      toast.success("Reports PDF downloaded");
    } catch (e) {
      toast.error("Could not export PDF");
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <div className="text-sm" style={{ color: "var(--text-muted)" }}>Loading reports…</div>;
  if (!data) return null;
  const S = ({ label, value, gold }) => (
    <div className="w-card w-card-bordered p-5">
      <div className="label-caps">{label}</div>
      <div className={`num-hero mt-1 ${gold ? "gold" : ""}`}>{Number(value).toLocaleString()}</div>
    </div>
  );
  return (
    <div className="space-y-6" data-testid="reports-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-caps gold">Snapshot</div>
          <h2 className="text-2xl font-bold" style={{ letterSpacing: "-0.02em" }}>
            Reports & Analytics
          </h2>
        </div>
        <Button
          type="button"
          onClick={exportPdf}
          disabled={exporting}
          data-testid="reports-pdf-export"
          className="rounded-full h-10 text-white"
          style={{ background: "var(--navy)" }}
        >
          {exporting ? "Preparing…" : "Export PDF"}
        </Button>
      </div>
      <div ref={panelRef} className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <S label="Total members" value={data.members.total} />
          <S label="Active members" value={data.members.active} />
          <S label="Points issued" value={data.points.issued} gold />
          <S label="Points redeemed" value={data.points.redeemed} />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="w-card w-card-bordered p-5">
            <div className="label-caps gold">Referrals</div>
            <div className="mt-3 flex items-baseline gap-6">
              <div>
                <div className="text-xs" style={{ color: "var(--text-muted)" }}>Signups via referral</div>
                <div className="num-big">{data.referrals.signups_via_referral}</div>
              </div>
              <div>
                <div className="text-xs" style={{ color: "var(--text-muted)" }}>Points paid out</div>
                <div className="num-big gold">{data.referrals.points_paid_out.toLocaleString()}</div>
              </div>
            </div>
          </div>
          <div className="w-card w-card-bordered p-5">
            <div className="label-caps gold">Earn by service</div>
            <ul className="mt-3 space-y-2 text-sm" data-testid="reports-by-service">
              {data.by_service.length === 0 && (
                <li style={{ color: "var(--text-muted)" }}>No earn transactions yet.</li>
              )}
              {data.by_service.map((s) => (
                <li key={s.service} className="flex justify-between">
                  <span>{s.service} <span style={{ color: "var(--text-muted)" }}>· {s.count} tx</span></span>
                  <span className="font-semibold">{s.points.toLocaleString()} pts</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        {data.profit && (
          <div className="w-card w-card-bordered p-5" data-testid="reports-profit">
            <div className="flex items-baseline justify-between gap-4 flex-wrap">
              <div>
                <div className="label-caps gold">Profit (internal)</div>
                <div className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>Approved, non-reversed earn transactions, converted to AED</div>
              </div>
              <div className="num-big gold" data-testid="reports-profit-total">AED {data.profit.total_aed.toLocaleString()}</div>
            </div>
            <ul className="mt-4 space-y-2 text-sm" data-testid="reports-profit-by-service">
              {data.profit.by_service.length === 0 && <li style={{ color: "var(--text-muted)" }}>No profit recorded yet.</li>}
              {data.profit.by_service.map((s) => (
                <li key={s.service} className="flex justify-between">
                  <span>{s.service} <span style={{ color: "var(--text-muted)" }}>· {s.count} tx</span></span>
                  <span className="font-semibold">AED {s.profit_aed.toLocaleString()}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="w-card w-card-bordered p-5">
          <div className="label-caps gold">Top earners</div>
          <ul className="mt-3 space-y-2 text-sm" data-testid="reports-top-earners">
            {data.top_earners.map((c) => (
              <li key={c.customer_id} className="flex items-center justify-between">
                <span>
                  <span className="font-medium">{c.name}</span>
                  <span className="ml-2 font-mono text-xs" style={{ color: "var(--text-muted)" }}>
                    {c.customer_id}
                  </span>
                </span>
                <span className="font-semibold gold">
                  {Number(c.lifetime_earned || 0).toLocaleString()} pts
                </span>
              </li>
            ))}
          </ul>
        </div>
        <OfferEnquiriesSection />
        <BookingEnquiriesSection />
      </div>
    </div>
  );
}

